import React, { useState, useEffect, useMemo } from 'react';
import { Download, MapPin, Clock, ChevronLeft, ChevronRight, BookOpen, Calendar as CalendarIcon, Printer } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { API } from '@/App';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import usePageContent from '@/hooks/usePageContent';

// German month names
const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const weekDays = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

// Hessische Feiertage - dynamisch berechnet
const getHessischeFeiertageFuerJahr = (year) => {
  // Ostersonntag berechnen (Gaußsche Osterformel)
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  const osterSonntag = new Date(year, month - 1, day);
  
  // Hilfsfunktion: Datum formatieren als YYYY-MM-DD
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  // Hilfsfunktion: Tage zu Datum addieren
  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };
  
  // Hessische gesetzliche Feiertage
  return {
    [formatDate(new Date(year, 0, 1))]: 'Neujahr',
    [formatDate(addDays(osterSonntag, -2))]: 'Karfreitag',
    [formatDate(addDays(osterSonntag, 1))]: 'Ostermontag',
    [formatDate(new Date(year, 4, 1))]: 'Tag der Arbeit',
    [formatDate(addDays(osterSonntag, 39))]: 'Christi Himmelfahrt',
    [formatDate(addDays(osterSonntag, 50))]: 'Pfingstmontag',
    [formatDate(addDays(osterSonntag, 60))]: 'Fronleichnam',
    [formatDate(new Date(year, 9, 3))]: 'Tag der Deutschen Einheit',
    [formatDate(new Date(year, 11, 25))]: '1. Weihnachtstag',
    [formatDate(new Date(year, 11, 26))]: '2. Weihnachtstag',
  };
};

const TheorieKalenderPage = () => {
  const { t: c } = usePageContent('theorie_kalender');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(2026);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [filterLocation, setFilterLocation] = useState('all');

  // Hessische Feiertage für das aktuelle Jahr berechnen
  const holidays = useMemo(() => getHessischeFeiertageFuerJahr(currentYear), [currentYear]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/calendar`);
      setEvents(res.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get days in a month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday, adjusted for Monday start)
  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1;
  };

  // Format date as YYYY-MM-DD
  const formatDate = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Get events for a specific date
  const getEventsForDate = (dateStr) => {
    return events.filter(event => event.start_date === dateStr);
  };

  // Get location color
  const getLocationColor = (location) => {
    if (location.includes('Schwanheim')) return 'bg-blue-500';
    if (location.includes('Niederrad')) return 'bg-green-500';
    if (location.includes('Offenbach')) return 'bg-orange-500';
    if (location.includes('Bad Homburg')) return 'bg-purple-500';
    return 'bg-gray-500';
  };

  const getLocationBgColor = (location) => {
    if (location.includes('Schwanheim')) return 'bg-blue-100 text-blue-800';
    if (location.includes('Niederrad')) return 'bg-green-100 text-green-800';
    if (location.includes('Offenbach')) return 'bg-orange-100 text-orange-800';
    if (location.includes('Bad Homburg')) return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Filter events by location
  const filteredEvents = useMemo(() => {
    if (filterLocation === 'all') return events;
    return events.filter(event => event.location.includes(filterLocation));
  }, [events, filterLocation]);

  // Render a single month calendar
  const renderMonth = (monthIndex) => {
    const daysInMonth = getDaysInMonth(currentYear, monthIndex);
    const firstDay = getFirstDayOfMonth(currentYear, monthIndex);
    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-slate-50"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDate(currentYear, monthIndex, day);
      const dayEvents = getEventsForDate(dateStr).filter(e => 
        filterLocation === 'all' || e.location.includes(filterLocation)
      );
      const isHoliday = holidays[dateStr];
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      const dayOfWeek = new Date(currentYear, monthIndex, day).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      days.push(
        <div 
          key={day} 
          className={`h-24 border border-slate-200 p-1 overflow-hidden ${
            isHoliday ? 'bg-red-50' : isWeekend ? 'bg-slate-50' : 'bg-white'
          } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className={`text-xs font-bold mb-1 ${
            isHoliday ? 'text-red-600' : isToday ? 'text-blue-600' : 'text-slate-700'
          }`}>
            {day}
            {isHoliday && <span className="text-red-500 ml-1 font-normal text-[10px]">{isHoliday}</span>}
          </div>
          <div className="space-y-0.5">
            {dayEvents.slice(0, 2).map((event, idx) => (
              <div 
                key={idx}
                className={`text-[10px] px-1 py-0.5 rounded truncate ${getLocationBgColor(event.location)}`}
                title={`${event.title} - ${event.start_time} Uhr - ${event.location}`}
              >
                {event.start_time} {event.title.replace('Theorieunterricht ', 'Th. ')}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div className="text-[10px] text-slate-500">+{dayEvents.length - 2} mehr</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Get short location label
  const getLocationShort = (location) => {
    if (location.includes('Schwanheim')) return 'SW';
    if (location.includes('Niederrad')) return 'NR';
    if (location.includes('Offenbach')) return 'OF';
    if (location.includes('Bad Homburg')) return 'BH';
    return '';
  };

  // Get location border color
  const getLocationBorderColor = (location) => {
    if (location.includes('Schwanheim')) return 'border-l-blue-500';
    if (location.includes('Niederrad')) return 'border-l-green-500';
    if (location.includes('Offenbach')) return 'border-l-orange-500';
    if (location.includes('Bad Homburg')) return 'border-l-purple-500';
    return 'border-l-gray-400';
  };

  // Get location dot color
  const getLocationDotColor = (location) => {
    if (location.includes('Schwanheim')) return 'bg-blue-500';
    if (location.includes('Niederrad')) return 'bg-green-500';
    if (location.includes('Offenbach')) return 'bg-orange-500';
    if (location.includes('Bad Homburg')) return 'bg-purple-500';
    return 'bg-gray-400';
  };

  // Render a single month in large view (for slider)
  const renderMonthLarge = (monthIndex) => {
    const daysInMonth = getDaysInMonth(currentYear, monthIndex);
    const firstDay = getFirstDayOfMonth(currentYear, monthIndex);
    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="min-h-[70px] sm:min-h-[110px] md:min-h-[130px] bg-slate-50/50 border border-slate-100"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDate(currentYear, monthIndex, day);
      const dayEvents = getEventsForDate(dateStr).filter(e => 
        filterLocation === 'all' || e.location.includes(filterLocation)
      );
      const isHoliday = holidays[dateStr];
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      const dayOfWeek = new Date(currentYear, monthIndex, day).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

      days.push(
        <div 
          key={day} 
          className={`min-h-[70px] sm:min-h-[110px] md:min-h-[130px] border border-slate-200 p-1 sm:p-1.5 md:p-2 overflow-hidden transition-colors ${
            isHoliday ? 'bg-red-50' : isWeekend ? 'bg-slate-50/50' : 'bg-white'
          } ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
        >
          <div className="flex items-center justify-between mb-0.5 sm:mb-1">
            <span className={`text-xs sm:text-sm font-bold ${
              isHoliday ? 'text-red-600' : isToday ? 'text-blue-600' : 'text-slate-800'
            }`}>
              {day}
            </span>
            {isHoliday && (
              <span className="text-[8px] sm:text-[10px] text-red-500 font-medium bg-red-100 px-1 sm:px-1.5 py-0.5 rounded-full leading-none">
                {isHoliday}
              </span>
            )}
          </div>
          <div className="space-y-0.5 sm:space-y-1">
            {dayEvents.slice(0, 2).map((event, idx) => (
              <div 
                key={idx}
                className={`border-l-2 sm:border-l-[3px] ${getLocationBorderColor(event.location)} bg-slate-50 rounded-r px-1 sm:px-2 py-0.5 sm:py-1 cursor-default hover:bg-slate-100 transition-colors`}
                title={`${event.title}\n${event.start_time} – ${event.end_time || ''} Uhr\n${event.location}${event.description ? '\n' + event.description : ''}`}
              >
                <div className="flex items-center gap-0.5 sm:gap-1">
                  <span className="text-[10px] sm:text-xs font-bold text-slate-900">{event.start_time}</span>
                  <span className={`ml-auto w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0 ${getLocationDotColor(event.location)}`}></span>
                </div>
                <div className="text-[9px] sm:text-[11px] text-slate-700 font-medium truncate leading-tight">
                  {event.title.replace('Theorieunterricht ', '')}
                </div>
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div className="text-[9px] sm:text-[11px] text-blue-600 font-medium pl-1">
                +{dayEvents.length - 2} weitere
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  // Download as PDF
  const downloadPDF = () => {
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text('Fahrschule Rößner - Theoriekalender ' + currentYear, 148.5, 15, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text('Tel: 069 20 32 60 59 | www.roessnerfahrschule.de | info@roessnerfahrschule.de', 148.5, 22, { align: 'center' });
    
    // Legend
    doc.setFontSize(9);
    doc.setTextColor(59, 130, 246);
    doc.text('● Schwanheim', 20, 30);
    doc.setTextColor(34, 197, 94);
    doc.text('● Niederrad', 60, 30);
    doc.setTextColor(249, 115, 22);
    doc.text('● Offenbach', 100, 30);
    doc.setTextColor(168, 85, 247);
    doc.text('● Bad Homburg', 140, 30);

    let yPos = 40;
    const pageHeight = 200;

    // Sort events by date
    const sortedEvents = [...filteredEvents].sort((a, b) => 
      new Date(a.start_date) - new Date(b.start_date)
    );

    // Group by month
    const groupedByMonth = {};
    sortedEvents.forEach(event => {
      const month = parseInt(event.start_date.split('-')[1]) - 1;
      if (!groupedByMonth[month]) groupedByMonth[month] = [];
      groupedByMonth[month].push(event);
    });

    Object.keys(groupedByMonth).forEach((monthIdx) => {
      const monthEvents = groupedByMonth[monthIdx];
      
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }

      // Month header
      doc.setFontSize(12);
      doc.setTextColor(15, 23, 42);
      doc.text(monthNames[monthIdx], 20, yPos);
      yPos += 7;

      // Events table
      const tableData = monthEvents.map(event => {
        const date = new Date(event.start_date);
        const dayName = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][date.getDay()];
        return [
          `${dayName}, ${date.getDate()}.${date.getMonth() + 1}.`,
          event.start_time + ' Uhr',
          event.title,
          event.location
        ];
      });

      autoTable(doc, {
        startY: yPos,
        head: [['Datum', 'Zeit', 'Thema', 'Standort']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8
        },
        bodyStyles: {
          fontSize: 8
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 20 },
          2: { cellWidth: 80 },
          3: { cellWidth: 50 }
        },
        margin: { left: 20, right: 20 }
      });

      yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 10 : yPos + 50;
    });

    // Footer on each page
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Seite ${i} von ${pageCount} | Stand: ${new Date().toLocaleDateString('de-DE')}`,
        148.5,
        205,
        { align: 'center' }
      );
    }

    doc.save(`Theoriekalender_${currentYear}.pdf`);
  };

  // Print calendar
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white print:bg-white">
      {/* Hero Section */}
      <section className="relative py-8 md:py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 overflow-hidden print:hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 relative z-10">
          <div className="text-center">
            <h1 className="text-2xl md:text-5xl font-bold text-white mb-2 md:mb-4">
              {c('hero_title', 'Theorie & Intensivkurse')}
            </h1>
            <p className="text-sm md:text-xl text-slate-300 max-w-2xl mx-auto">
              {c('hero_subtitle', 'Alle Termine für Theorieunterricht an unseren Standorten')}
            </p>
          </div>
        </div>
      </section>

      {/* Controls */}
      <section className="py-2 md:py-6 border-b bg-white sticky top-20 z-30 print:hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex bg-slate-100 rounded-lg p-0.5">
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  className="text-xs h-7 px-2 md:text-sm md:h-9 md:px-3"
                  onClick={() => setViewMode('calendar')}
                >
                  <CalendarIcon className="w-3.5 h-3.5 mr-1" />
                  {c('btn_kalender', 'Kalender')}
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  className="text-xs h-7 px-2 md:text-sm md:h-9 md:px-3"
                  onClick={() => setViewMode('list')}
                >
                  <BookOpen className="w-3.5 h-3.5 mr-1" />
                  {c('btn_liste', 'Liste')}
                </Button>
              </div>

              {/* Year Navigation (only shown in list view) */}
              {viewMode === 'list' && (
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-7 w-7 md:h-9 md:w-9" onClick={() => setCurrentYear(y => y - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm md:text-xl font-bold text-slate-900 min-w-[40px] text-center">{currentYear}</span>
                  <Button variant="outline" size="icon" className="h-7 w-7 md:h-9 md:w-9" onClick={() => setCurrentYear(y => y + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Location Filter */}
              <Select value={filterLocation} onValueChange={setFilterLocation}>
                <SelectTrigger className="w-28 md:w-48 h-7 md:h-9 text-xs md:text-sm">
                  <SelectValue placeholder="Alle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Standorte</SelectItem>
                  <SelectItem value="Schwanheim">Schwanheim</SelectItem>
                  <SelectItem value="Niederrad">Niederrad</SelectItem>
                  <SelectItem value="Offenbach">Offenbach</SelectItem>
                  <SelectItem value="Bad Homburg">Bad Homburg</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="hidden sm:flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint} className="text-xs md:text-sm">
                <Printer className="w-4 h-4 mr-1 md:mr-2" />
                {c('btn_drucken', 'Drucken')}
              </Button>
              <Button size="sm" onClick={downloadPDF} className="bg-blue-600 hover:bg-blue-500 text-xs md:text-sm">
                <Download className="w-4 h-4 mr-1 md:mr-2" />
                {c('btn_pdf', 'PDF')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Legend - compact on mobile */}
      <section className="py-2 md:py-4 bg-white border-b print:py-2">
        <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 md:gap-6 text-xs md:text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-blue-500 flex-shrink-0"></div>
              <span>{c('legende_schwanheim', 'SW (Mo & Fr)')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-green-500 flex-shrink-0"></div>
              <span>{c('legende_niederrad', 'NR (Mi)')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-orange-500 flex-shrink-0"></div>
              <span>{c('legende_offenbach', 'OF')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-purple-500 flex-shrink-0"></div>
              <span>{c('legende_badhomburg', 'BH')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded bg-red-100 border border-red-300 flex-shrink-0"></div>
              <span className="text-red-600">{c('legende_feiertage', 'Feiertage')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar */}
      <section className="py-4 md:py-8">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 md:px-12 lg:px-24">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : viewMode === 'calendar' ? (
            // Calendar View - Single month with slider navigation
            <div>
              {/* Month Navigation */}
              <div className="flex items-center justify-center gap-3 sm:gap-6 mb-3 sm:mb-6">
                <Button 
                  variant="outline" 
                  size="icon"
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full"
                  onClick={() => {
                    if (selectedMonth === 0) {
                      setSelectedMonth(11);
                      setCurrentYear(y => y - 1);
                    } else {
                      setSelectedMonth(m => m - 1);
                    }
                  }}
                  data-testid="calendar-prev-month"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </Button>
                <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-slate-900 min-w-[180px] sm:min-w-[280px] text-center">
                  {monthNames[selectedMonth]} {currentYear}
                </h2>
                <Button 
                  variant="outline" 
                  size="icon"
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full"
                  onClick={() => {
                    if (selectedMonth === 11) {
                      setSelectedMonth(0);
                      setCurrentYear(y => y + 1);
                    } else {
                      setSelectedMonth(m => m + 1);
                    }
                  }}
                  data-testid="calendar-next-month"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Month indicator dots */}
              <div className="flex justify-center gap-1 sm:gap-1.5 mb-3 sm:mb-6">
                {monthNames.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedMonth(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      idx === selectedMonth 
                        ? 'bg-blue-600 scale-125' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                    title={monthNames[idx]}
                    data-testid={`calendar-dot-${idx}`}
                  />
                ))}
              </div>

              <Card className="overflow-hidden max-w-4xl mx-auto">
                <CardContent className="p-0">
                  {/* Week days header */}
                  <div className="grid grid-cols-7 text-center text-xs sm:text-sm font-semibold bg-slate-900">
                    {weekDays.map(day => (
                      <div key={day} className="py-2 sm:py-3 text-white">{day}</div>
                    ))}
                  </div>
                  {/* Days grid */}
                  <div className="grid grid-cols-7">
                    {renderMonthLarge(selectedMonth)}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            // List View - Show all events grouped by month
            <Card>
              <div className="bg-slate-900 text-white px-6 py-4 font-bold text-xl">
                Alle Termine {currentYear}
              </div>
              <CardContent className="p-0">
                {monthNames.map((monthName, monthIdx) => {
                  const monthEvents = filteredEvents
                    .filter(event => {
                      const eventYear = parseInt(event.start_date.split('-')[0]);
                      const eventMonth = parseInt(event.start_date.split('-')[1]) - 1;
                      return eventYear === currentYear && eventMonth === monthIdx;
                    })
                    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

                  if (monthEvents.length === 0) return null;

                  return (
                    <div key={monthIdx}>
                      <div className="bg-slate-100 px-6 py-3 font-semibold text-slate-700 border-b border-t">
                        {monthName} {currentYear}
                      </div>
                      <div className="divide-y">
                        {monthEvents.map((event, idx) => {
                          const date = new Date(event.start_date);
                          const dayName = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][date.getDay()];
                          const isHoliday = holidays[event.start_date];
                          
                          return (
                            <div key={idx} className={`flex items-center gap-4 px-6 py-4 hover:bg-slate-50 ${isHoliday ? 'bg-red-50' : ''}`}>
                              <div className={`w-2 h-12 rounded ${getLocationColor(event.location)}`}></div>
                              <div className="w-28">
                                <div className="font-semibold text-slate-900">
                                  {dayName}, {date.getDate()}.{date.getMonth() + 1}.
                                </div>
                                {isHoliday && (
                                  <div className="text-xs text-red-600">{isHoliday}</div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 w-24">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span className="font-medium">{event.start_time} Uhr</span>
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-slate-900">{event.title}</div>
                                {event.description && (
                                  <div className="text-sm text-slate-500">{event.description}</div>
                                )}
                              </div>
                              <Badge className={getLocationBgColor(event.location)}>
                                <MapPin className="w-3 h-3 mr-1" />
                                {event.location}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {filteredEvents.filter(e => parseInt(e.start_date.split('-')[0]) === currentYear).length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    Keine Termine für {currentYear} gefunden
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-8 bg-slate-900 text-white print:hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold mb-2">{c('contact_title', 'Fragen zum Unterricht?')}</h3>
              <p className="text-slate-400">{c('contact_subtitle', 'Wir beraten Sie gerne zu unseren Theoriekursen.')}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-sm text-slate-400">Telefon</div>
                <div className="font-bold text-lg">069 20 32 60 59</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-slate-400">E-Mail</div>
                <div className="font-bold">info@roessnerfahrschule.de</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
          .print\\:grid-cols-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
          .print\\:bg-white { background: white !important; }
        }
      `}</style>
    </div>
  );
};

export default TheorieKalenderPage;
